import type { PropBehaviour, WorldDocument } from "../document/types.ts";
import { asRecord } from "../document/validate.ts";
import type { StripPlays } from "../editor/strips.ts";
import { frameId, type FrameAnimation } from "./manifest.ts";

/**
 * A strip's timing (ADR-107 decisions 20 and 21): how long each frame shows, a rest added to the first or the
 * last frame, and whether the strip loops or plays once. It is lowered into the manifest's per-frame
 * durations, which `animationFrameAt` already walks, so the manifest's layout does not change. A strip with no
 * timing shows each frame for `STRIP_FRAME_MS`. The bounds are the studio document's (decision 3).
 */

/**
 * The most frames of an animation.
 */
export const ANIMATION_FRAMES_MAX = 64;

/**
 * The shortest time a frame shows, in milliseconds.
 */
export const FRAME_MS_MIN = 16;

/**
 * The longest time a frame shows, in milliseconds.
 */
export const FRAME_MS_MAX = 10_000;

/**
 * The longest rest a strip holds, in milliseconds.
 */
export const REST_MS_MAX = 60_000;

/**
 * How long each frame of a strip shows when nothing says otherwise, in milliseconds.
 */
export const STRIP_FRAME_MS = 100;

const TIMING_FIELDS = ["frameMs", "restMs", "restOn", "plays"];

/**
 * A strip's timing as a frame override holds it: every field optional. `frameMs` is one time for every frame
 * or a list of one per frame; `restOn` is the index of the frame the rest is added to, any frame of the strip
 * (the author's ruling of 2026-10-07), 0 by default.
 */
export interface StripTiming {
  readonly frameMs?: number | readonly number[];
  readonly restMs?: number;
  readonly restOn?: number;
  readonly plays?: StripPlays;
}

/**
 * Where a once strip is played by transitions: each world with the number of its props whose behaviour plays
 * it, and each template and preset that plays it, by name.
 */
export interface StripUses {
  readonly worlds: readonly { readonly name: string; readonly count: number }[];
  readonly templates: readonly string[];
  readonly presets: readonly string[];
}

/**
 * The durations of an animation's frames: each frame's time, with the rest added to the frame it names.
 *
 * @param frameMs Each frame's time in milliseconds.
 * @param restMs The rest in milliseconds.
 * @param restOn The index of the frame the rest is added to.
 * @returns One duration per frame.
 * @throws RangeError when `restOn` is not a frame of the strip.
 */
export function animationTiming(frameMs: readonly number[], restMs: number, restOn: number): number[] {
  if (!Number.isInteger(restOn) || restOn < 0 || restOn >= frameMs.length) {
    throw new RangeError(`timing.restOn is frame ${restOn} of a strip of ${frameMs.length} frames`);
  }
  const durations = [...frameMs];
  durations[restOn] = (durations[restOn] as number) + restMs;
  return durations;
}

/**
 * A strip's durations and how it plays, from its frame count, how its frames say it plays and its timing.
 *
 * @param frames The strip's frame count.
 * @param plays How the strip plays when its timing does not say.
 * @param timing The timing, or undefined for the default.
 * @returns The durations and how it plays.
 * @throws Error when a list of frame times does not hold one per frame, and RangeError when the rest names a
 *   frame past the strip.
 */
export function stripTiming(frames: number, plays: StripPlays, timing: StripTiming | undefined): { readonly durations: number[]; readonly plays: StripPlays } {
  const given = timing?.frameMs ?? STRIP_FRAME_MS;
  if (typeof given !== "number" && given.length !== frames) {
    throw new Error(`timing.frameMs holds ${given.length} durations for a strip of ${frames} frames`);
  }
  const frameMs = typeof given === "number" ? Array.from({ length: frames }, () => given) : [...given];
  return { durations: animationTiming(frameMs, timing?.restMs ?? 0, timing?.restOn ?? 0), plays: timing?.plays ?? plays };
}

/**
 * Reads a strip's timing within the studio document's bounds.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The timing.
 * @throws Error naming the first fault by its path.
 */
export function readStripTiming(value: unknown, path: string): StripTiming {
  const record = asRecord(value, path);
  for (const key of Object.keys(record)) {
    if (!TIMING_FIELDS.includes(key)) {
      throw new Error(`${path}.${key} is not a field of a strip's timing`);
    }
  }
  if (TIMING_FIELDS.every((key) => record[key] === undefined)) {
    throw new Error(`${path} must set frameMs, restMs, restOn or plays`);
  }
  let timing: StripTiming = {};
  for (const key of TIMING_FIELDS) {
    if (record[key] !== undefined) {
      timing = { ...timing, [key]: timingField(key, record[key], `${path}.${key}`) };
    }
  }
  return timing;
}

/**
 * The part of a stored timing this build takes: each field `readStripTiming` would read, any other passed
 * over. A field this build knows that breaks a bound is left out and named among the faults; a field it does
 * not know, which a later build may write, is passed over in silence.
 *
 * @param value The stored timing, as parsed.
 * @returns The usable timing, empty when nothing is usable, and one fault per field left out.
 */
export function usableStripTiming(value: unknown): { readonly timing: StripTiming; readonly faults: readonly string[] } {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return { timing: {}, faults: ["timing must be an object"] };
  }
  const record = value as Record<string, unknown>;
  let timing: StripTiming = {};
  const faults: string[] = [];
  for (const key of TIMING_FIELDS) {
    if (record[key] === undefined) {
      continue;
    }
    try {
      timing = { ...timing, [key]: timingField(key, record[key], `timing.${key}`) };
    } catch (error) {
      faults.push((error as Error).message);
    }
  }
  return { timing, faults };
}

/**
 * Reads one field of a strip's timing within the studio document's bounds.
 *
 * @param key The field's name, one of `TIMING_FIELDS`.
 * @param value Its value.
 * @param path Path for error messages.
 * @returns The value read.
 * @throws Error naming the path when it is out of bounds.
 */
function timingField(key: string, value: unknown, path: string): StripTiming[keyof StripTiming] {
  if (key === "frameMs") {
    if (!Array.isArray(value)) {
      return frameTime(value, path);
    }
    if (value.length < 1 || value.length > ANIMATION_FRAMES_MAX) {
      throw new Error(`${path} must hold 1 to ${ANIMATION_FRAMES_MAX} durations`);
    }
    return value.map((ms, i) => frameTime(ms, `${path}[${i}]`));
  }
  if (key === "restMs") {
    if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > REST_MS_MAX) {
      throw new Error(`${path} must be an integer in [0, ${REST_MS_MAX}]`);
    }
    return value;
  }
  if (key === "restOn") {
    if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value >= ANIMATION_FRAMES_MAX) {
      throw new Error(`${path} must be a whole number from 0 to ${ANIMATION_FRAMES_MAX - 1}`);
    }
    return value;
  }
  if (value !== "loop" && value !== "once") {
    throw new Error(`${path} must be loop or once`);
  }
  return value;
}

/**
 * Reports whether a behaviour plays a strip: one of its named animations starts at the strip's first frame.
 *
 * @param behaviour The behaviour, or undefined.
 * @param strip The frame id of the strip's first frame.
 * @returns True when it does.
 */
export function behaviourPlays(behaviour: PropBehaviour | undefined, strip: string): boolean {
  return Object.values(behaviour?.animations ?? {}).some((start) => frameId(start.tileset, null, start.tile) === strip);
}

/**
 * The number of props of a world whose behaviour plays a strip.
 *
 * @param world The world.
 * @param strip The frame id of the strip's first frame.
 * @returns The count.
 */
export function transitionsNaming(world: WorldDocument, strip: string): number {
  let count = 0;
  for (const map of world.maps) {
    for (const layer of map.layers) {
      if (layer.kind === "prop") {
        count += layer.props.filter((prop) => behaviourPlays(prop.behaviour, strip)).length;
      }
    }
  }
  return count;
}

/**
 * What a strip's uses are gathered from: each world with the name it is listed by, and each template and preset
 * with its name and the behaviours it holds.
 */
export interface StripSources {
  readonly worlds: readonly { readonly name: string; readonly document: WorldDocument }[];
  readonly templates: readonly { readonly name: string; readonly pieces: readonly { readonly behaviour?: PropBehaviour }[] }[];
  readonly presets: readonly { readonly name: string; readonly behaviour: PropBehaviour }[];
}

/**
 * Where transitions play a strip (ADR-107 decision 21): each world that holds a prop whose behaviour plays it,
 * with the count, and each template and preset holding such a behaviour, by name, in the order given. A world,
 * template or preset that does not play it is left out.
 *
 * @param strip The frame id of the strip's first frame.
 * @param sources The worlds, templates and presets to look through.
 * @returns The uses.
 */
export function stripUses(strip: string, sources: StripSources): StripUses {
  return {
    worlds: sources.worlds.map((world) => ({ name: world.name, count: transitionsNaming(world.document, strip) })).filter((world) => world.count > 0),
    templates: sources.templates.filter((template) => template.pieces.some((piece) => behaviourPlays(piece.behaviour, strip))).map((template) => template.name),
    presets: sources.presets.filter((preset) => behaviourPlays(preset.behaviour, strip)).map((preset) => preset.name),
  };
}

/**
 * The number of props of a world placed with a strip's first frame, which are the copies that loop while the
 * strip does and stop when it is turned to play once.
 *
 * @param world The world.
 * @param strip The frame id of the strip's first frame.
 * @returns The count.
 */
export function placedCopies(world: WorldDocument, strip: string): number {
  let count = 0;
  for (const map of world.maps) {
    for (const layer of map.layers) {
      if (layer.kind === "prop") {
        count += layer.props.filter((prop) => frameId(prop.frame.tileset, null, prop.frame.tile) === strip).length;
      }
    }
  }
  return count;
}

/**
 * A strip's animation as a manifest holds it, from its frames, how the pack says it plays and its timing: what the
 * asset build writes for the strip, so an editor that reads a stored timing shows what the next build will.
 *
 * @param frames The strip's frame ids, in order.
 * @param plays How the strip plays when its timing does not say.
 * @param timing The timing, or undefined for the default.
 * @returns The animation, marked `once` when it plays once.
 * @throws Error or RangeError when the timing does not fit the strip, as `stripTiming` throws.
 */
export function timedAnimation(frames: readonly string[], plays: StripPlays, timing: StripTiming | undefined): FrameAnimation {
  const timed = stripTiming(frames.length, plays, timing);
  return { frames, durations: timed.durations, ...(timed.plays === "once" ? { once: true as const } : {}) };
}

/**
 * An editor's animations with a pack's stored timings lowered into them, as the next build will write them
 * (ADR-107's note on P1.14-T11, item 6): each animation whose first frame holds a stored timing takes that
 * timing's durations and how it plays. How a strip plays when its timing does not say is how the animation given
 * plays, the build's word for it. A timing on a frame that starts no animation here is passed over; a field of a
 * timing that breaks a bound is left out as `usableStripTiming` leaves it; a timing that does not fit its strip is
 * named among the faults and leaves the animation as it was.
 *
 * @param animations The editor's animations, by the frame id of each one's first frame.
 * @param overrides A pack's stored overrides, by frame id.
 * @returns The animations, every untouched one the same object, and one fault for each timing not taken.
 */
export function applyStripTimings(animations: Readonly<Record<string, FrameAnimation>>, overrides: Readonly<Record<string, unknown>>): { readonly animations: Readonly<Record<string, FrameAnimation>>; readonly faults: readonly string[] } {
  let timed: Record<string, FrameAnimation> | null = null;
  const faults: string[] = [];
  for (const [id, override] of Object.entries(overrides)) {
    const animation = animations[id];
    const stored = typeof override === "object" && override !== null ? (override as { readonly timing?: unknown }).timing : undefined;
    if (animation === undefined || stored === undefined || stored === null) {
      continue;
    }
    try {
      const next = timedAnimation(animation.frames, animation.once === true ? "once" : "loop", usableStripTiming(stored).timing);
      timed ??= { ...animations };
      timed[id] = next;
    } catch (error) {
      faults.push(`${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  return { animations: timed ?? animations, faults };
}

/**
 * The most worlds, templates and presets a refusal names; the rest are counted ("and 6 more").
 */
export const STRIP_USES_NAMED = 8;

/**
 * A stored timing, or any JSON value, as text with its keys in order at every depth and no space, so two values that
 * hold the same things read the same. A caller that names a stored timing by digest hashes this text.
 *
 * @param value The value.
 * @returns The text.
 */
export function canonicalTiming(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalTiming).join(",")}]`;
  }
  if (typeof value === "object" && value !== null) {
    const record = value as Readonly<Record<string, unknown>>;
    return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${canonicalTiming(record[key])}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

/**
 * Whether a stored timing is the one a save was based on, compared as it is stored and never read: the same fields
 * with the same values at every depth, whatever their order, a field this build does not know and a value out of
 * bounds included. So a stored timing a reader would refuse can still be named as the base of the save that
 * replaces it. No timing is the same as no timing only.
 *
 * @param stored The stored timing as parsed, or null or undefined for none.
 * @param basedOn What the save says it was based on, as parsed.
 * @returns True when they hold the same.
 */
export function sameStoredTiming(stored: unknown, basedOn: unknown): boolean {
  const text = (value: unknown): string => (value === null || value === undefined ? "none" : canonicalTiming(value));
  return text(stored) === text(basedOn);
}

/**
 * Whether two timings are the same: the same fields with the same values, whatever their order. No timing is the
 * same as no timing and as nothing else, an empty timing included, since a save names the stored timing it was
 * based on exactly.
 *
 * @param a One timing, or null or undefined for none.
 * @param b The other.
 * @returns True when they are the same.
 */
export function sameTiming(a: StripTiming | null | undefined, b: StripTiming | null | undefined): boolean {
  const text = (timing: StripTiming | null | undefined): string => (timing === null || timing === undefined ? "none" : JSON.stringify(TIMING_FIELDS.map((field) => (timing as Readonly<Record<string, unknown>>)[field] ?? null)));
  return text(a) === text(b);
}

/**
 * Refuses turning a strip that plays once into a loop while any transition plays it: a transition may play
 * only a once strip, and every world naming it would stop compiling. Loop to once, and once to loop with no
 * transition playing it, are allowed.
 *
 * @param name The strip's name, for the message.
 * @param from How it plays now.
 * @param to How it would play.
 * @param uses Where transitions play it.
 * @throws Error listing each world, template and preset that plays it, the first `STRIP_USES_NAMED` by name and the
 *   rest by their count.
 */
export function refuseOnceToLoop(name: string, from: StripPlays, to: StripPlays, uses: StripUses): void {
  if (from !== "once" || to !== "loop") {
    return;
  }
  const every = [
    ...uses.worlds.filter((world) => world.count > 0).map((world) => ({ world: true, words: `${world.name} (${world.count})` })),
    ...uses.templates.map((template) => ({ world: false, words: `the template ${template}` })),
    ...uses.presets.map((preset) => ({ world: false, words: `the preset ${preset}` })),
  ];
  if (every.length === 0) {
    return;
  }
  const named = every.slice(0, STRIP_USES_NAMED);
  const more = every.length - named.length;
  const worlds = named.filter((place) => place.world).map((place) => place.words);
  const places = [...(worlds.length === 0 ? [] : [`transitions in ${worlds.join(", ")}`]), ...named.filter((place) => !place.world).map((place) => place.words)];
  const listed = more > 0 ? `${places.join(", ")} and ${more} more` : places.length === 1 ? places[0] : `${places.slice(0, -1).join(", ")} and ${places[places.length - 1]}`;
  throw new Error(`${name} is played by ${listed}; change those first`);
}

/**
 * Reads one frame time.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The time.
 * @throws Error when it is not a whole number of milliseconds in bounds.
 */
function frameTime(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < FRAME_MS_MIN || value > FRAME_MS_MAX) {
    throw new Error(`${path} must be an integer in [${FRAME_MS_MIN}, ${FRAME_MS_MAX}]`);
  }
  return value;
}
